import { writeJson, readJson, pathExists } from '@wix/cli-fs';
import { CliError, CliErrorCode } from '@wix/cli-error';
import { getTestOverrides } from '@wix/cli-test-overrides';
import { join } from 'node:path';
import type { VariantOf } from 'variant';
import { fields, variant } from 'variant';
import { z } from 'zod';

export const CacheState = variant({
  Exists: fields<{ data: unknown }>(),
  NotExists: {},
});

export type CacheState = VariantOf<typeof CacheState>;

const CacheData = z.object({
  data: z.unknown(),
  createdAt: z.number(),
});

type CacheData = z.infer<typeof CacheData>;

interface CliCacheOptions {
  filePath: string;
  ttlInHours?: number;
}

const isFileExpired = (createdAt: number, ttlInHours: number) => {
  return Date.now() - createdAt.valueOf() > ttlInHours * 60 * 60 * 1000;
};

export function getFsCacheFilePath(dirPath: string, fileName: string) {
  const testOverrides = getTestOverrides();

  if (testOverrides.fsCacheDir) {
    return join(testOverrides.fsCacheDir, fileName);
  }

  return join(dirPath, fileName);
}

export function createFsCache({
  filePath,
  ttlInHours = 24 * 7,
}: CliCacheOptions) {
  const get = async (): Promise<CacheState> => {
    try {
      const isCacheExists = await pathExists(filePath);

      if (isCacheExists) {
        const cacheData = CacheData.parse(await readJson(filePath));

        if (!isFileExpired(cacheData.createdAt, ttlInHours)) {
          return CacheState.Exists({ data: cacheData.data });
        }
      }

      return CacheState.NotExists();
    } catch (e) {
      throw new CliError({
        code: CliErrorCode.FailedToReadCache({ path: filePath }),
        cause: e,
      });
    }
  };

  const set = async (data: unknown): Promise<CacheState> => {
    try {
      await writeJson(filePath, {
        data,
        createdAt: Date.now(),
      });
      return CacheState.Exists({ data });
    } catch (e) {
      throw new CliError({
        code: CliErrorCode.FailedToWriteCache({ path: filePath }),
        cause: e,
      });
    }
  };

  return {
    get,
    set,
  };
}
